import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Alert,
  TablePagination,
  TableSortLabel,
  Chip,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Business as BusinessIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import api from '../../services/api';
import Layout from '../Layout';

const DeParaPanel = () => {
  // Estados principais
  const [mapeamentos, setMapeamentos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [seguradoras, setSeguradoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMapeamento, setEditingMapeamento] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Estados do formulário
  const [formData, setFormData] = useState({
    empresa_id: '',
    seguradora_id: '',
    razao_social_seguradora: ''
  });

  // Estados de paginação e pesquisa
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('empresa_nome');
  const [order, setOrder] = useState('asc');

  // Estados de filtros
  const [filtroEmpresa, setFiltroEmpresa] = useState(null);
  const [filtroSeguradora, setFiltroSeguradora] = useState(null);

  // Carregar dados
  const fetchMapeamentos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/empresa-seguradora');
      setMapeamentos(response.data || []);
    } catch (err) {
      console.error('Erro ao buscar mapeamentos:', err);
      setError('Erro ao carregar mapeamentos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      const raw = response.data;
      const lista = Array.isArray(raw) ? raw : Array.isArray(raw?.empresas) ? raw.empresas : [];
      const byId = new Map();
      for (const e of lista) {
        const id = e?.id ?? e?.ID ?? e?.Id;
        if (!id) continue;
        const nomeRaw = (
          e?.["Nome Fantasia"] ??
          e?.NomeFantasia ??
          e?.RazaoSocial ??
          e?.nome ??
          e?.Nome ??
          id
        );
        const nomeFantasia = String(nomeRaw).trim();
        const nome = `${id} - ${nomeFantasia}`; // Formato: "ID - NOME"
        if (!byId.has(id)) byId.set(id, { id, nome, nomeFantasia });
      }
      const normalizadas = Array.from(byId.values()).sort((a, b) => a.id - b.id); // Ordenar por ID
      setEmpresas(normalizadas);
    } catch (err) {
      console.error('Erro ao buscar empresas:', err);
    }
  };

  const fetchSeguradoras = async () => {
    try {
      const response = await api.get('/seguradoras');
      const raw = response.data;
      const lista = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : Array.isArray(raw?.results) ? raw.results : [];
      setSeguradoras(lista);
    } catch (err) {
      console.error('Erro ao buscar seguradoras:', err);
    }
  };

  useEffect(() => {
    Promise.all([
      fetchMapeamentos(),
      fetchEmpresas(),
      fetchSeguradoras()
    ]);
  }, []);

  // Handlers do formulário
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      empresa_id: '',
      seguradora_id: '',
      razao_social_seguradora: ''
    });
    setEditingMapeamento(null);
  };

  const handleOpenDialog = (mapeamento = null) => {
    if (mapeamento) {
      setEditingMapeamento(mapeamento);
      setFormData({
        empresa_id: mapeamento.empresa_id || '',
        seguradora_id: mapeamento.seguradora_id || '',
        razao_social_seguradora: mapeamento.razao_social_seguradora || ''
      });
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
    setError('');
  };

  // Salvar mapeamento
  const handleSave = async () => {
    try {
      if (!formData.empresa_id) {
        setError('Empresa é obrigatória');
        return;
      }

      if (!formData.seguradora_id) {
        setError('Seguradora é obrigatória');
          return;
        }

      if (!formData.razao_social_seguradora.trim()) {
        setError('Razão social da seguradora é obrigatória');
        return;
      }

      const payload = {
        empresa_id: parseInt(formData.empresa_id),
        seguradora_id: parseInt(formData.seguradora_id),
        razao_social_seguradora: formData.razao_social_seguradora.trim()
      };

      if (editingMapeamento) {
        await api.put(`/empresa-seguradora/${editingMapeamento.id}`, payload);
        setSuccess('Mapeamento atualizado com sucesso!');
      } else {
        await api.post('/empresa-seguradora', payload);
        setSuccess('Mapeamento criado com sucesso!');
      }

      handleCloseDialog();
      fetchMapeamentos();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Erro ao salvar mapeamento:', err);
      setError(err.response?.data?.message || 'Erro ao salvar mapeamento');
    }
  };

  // Excluir mapeamento
  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este mapeamento?')) {
      return;
    }

    try {
      await api.delete(`/empresa-seguradora/${id}`);
      setSuccess('Mapeamento excluído com sucesso!');
      fetchMapeamentos();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Erro ao excluir mapeamento:', err);
      setError(err.response?.data?.message || 'Erro ao excluir mapeamento');
    }
  };

  // Filtrar e ordenar dados
  const filteredMapeamentos = mapeamentos
    .filter(map => {
      // Filtro por texto de busca
      const matchesSearch = searchTerm === '' || 
        map.empresa_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        map.seguradora_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        map.razao_social_seguradora?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtro por empresa
      const matchesEmpresa = !filtroEmpresa || map.empresa_id === filtroEmpresa.id;
      
      // Filtro por seguradora
      const matchesSeguradora = !filtroSeguradora || map.seguradora_id === filtroSeguradora.id;
      
      return matchesSearch && matchesEmpresa && matchesSeguradora;
    })
    .sort((a, b) => {
      const aValue = a[orderBy] || '';
      const bValue = b[orderBy] || '';
      if (order === 'asc') {
        return aValue.localeCompare(bValue);
      }
      return bValue.localeCompare(aValue);
    });

  const paginatedMapeamentos = filteredMapeamentos.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFiltroEmpresa(null);
    setFiltroSeguradora(null);
  };

  return (
    <Layout>
      <Container maxWidth="xl">
        <Box sx={{ py: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              De/Para - Mapeamento Empresa-Seguradora
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Configure como cada empresa aparece nos arquivos de cada seguradora.
              Este sistema substitui a tabela DePara_RazaoSocial por um modelo dinâmico e relacional.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

          {/* Filtros e Pesquisa */}
          <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                  label="Pesquisar"
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    endAdornment: searchTerm && (
                      <IconButton onClick={() => setSearchTerm('')} size="small">
                        <ClearIcon />
                      </IconButton>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={empresas}
                  getOptionLabel={(option) => option.NomeFantasia || option.nome || ''}
                  value={filtroEmpresa}
                  onChange={(event, newValue) => setFiltroEmpresa(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filtrar por Empresa"
                InputProps={{
                        ...params.InputProps,
                        startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                    />
                  )}
              />
            </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={seguradoras}
                  getOptionLabel={(option) => option.nome || ''}
                  value={filtroSeguradora}
                  onChange={(event, newValue) => setFiltroSeguradora(newValue)}
                  renderInput={(params) => (
              <TextField
                      {...params}
                      label="Filtrar por Seguradora"
                InputProps={{
                        ...params.InputProps,
                        startAdornment: <AccountBalanceIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                    />
                  )}
              />
            </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                    variant="outlined"
                    onClick={clearFilters}
                startIcon={<ClearIcon />}
                size="small"
                  >
                    Limpar
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                fullWidth
              >
                    Novo Mapeamento
              </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

          {/* Estatísticas */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {mapeamentos.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total de Mapeamentos
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="secondary">
                  {new Set(mapeamentos.map(m => m.empresa_id)).size}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Empresas Mapeadas
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="success.main">
                  {new Set(mapeamentos.map(m => m.seguradora_id)).size}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Seguradoras Ativas
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Tabela de Mapeamentos */}
          <Paper>
            <TableContainer>
              <Table>
              <TableHead>
                <TableRow>
                    <TableCell>
                    <TableSortLabel
                        active={orderBy === 'empresa_nome'}
                        direction={orderBy === 'empresa_nome' ? order : 'asc'}
                        onClick={() => handleRequestSort('empresa_nome')}
                      >
                        Empresa
                    </TableSortLabel>
                  </TableCell>
                    <TableCell>
                    <TableSortLabel
                        active={orderBy === 'seguradora_nome'}
                        direction={orderBy === 'seguradora_nome' ? order : 'asc'}
                        onClick={() => handleRequestSort('seguradora_nome')}
                      >
                        Seguradora
                    </TableSortLabel>
                  </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'razao_social_seguradora'}
                        direction={orderBy === 'razao_social_seguradora' ? order : 'asc'}
                        onClick={() => handleRequestSort('razao_social_seguradora')}
                      >
                        Razão Social na Seguradora
                      </TableSortLabel>
                  </TableCell>
                    <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Box sx={{ py: 3 }}>
                    <CircularProgress />
                          <Typography sx={{ mt: 1 }}>Carregando mapeamentos...</Typography>
                        </Box>
                  </TableCell>
                </TableRow>
                  ) : paginatedMapeamentos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Box sx={{ py: 6 }}>
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            {searchTerm || filtroEmpresa || filtroSeguradora 
                              ? 'Nenhum mapeamento encontrado com os filtros aplicados' 
                              : 'Nenhum mapeamento cadastrado'
                            }
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {searchTerm || filtroEmpresa || filtroSeguradora
                              ? 'Tente limpar os filtros ou ajustar os critérios de busca'
                              : 'Comece criando seu primeiro mapeamento empresa-seguradora'
                            }
                          </Typography>
                          {!(searchTerm || filtroEmpresa || filtroSeguradora) && (
                            <Button 
                              variant="contained" 
                              startIcon={<AddIcon />}
                              onClick={() => handleOpenDialog()}
                            >
                              Criar Primeiro Mapeamento
                            </Button>
                          )}
                        </Box>
                    </TableCell>
                    </TableRow>
                  ) : (
                    paginatedMapeamentos.map((mapeamento) => (
                      <TableRow key={mapeamento.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon color="action" fontSize="small" />
                            <Typography variant="body2" fontWeight="medium">
                              {mapeamento.empresa_nome}
                            </Typography>
                          </Box>
                    </TableCell>
                    <TableCell>
                        <Chip 
                            label={mapeamento.seguradora_nome} 
                            color="primary" 
                            variant="outlined" 
                          size="small" 
                            icon={<AccountBalanceIcon />}
                        />
                    </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            component="code" 
                      sx={{ 
                              backgroundColor: 'grey.100', 
                              p: 0.5, 
                              borderRadius: 1,
                              fontSize: '0.875rem'
                            }}
                          >
                            {mapeamento.razao_social_seguradora}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                      <IconButton
                            onClick={() => handleOpenDialog(mapeamento)}
                        color="primary"
                        size="small"
                            title="Editar mapeamento"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                            onClick={() => handleDelete(mapeamento.id)}
                        color="error"
                        size="small"
                            title="Excluir mapeamento"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
                      </Table>
          </TableContainer>
          
          <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
              count={filteredMapeamentos.length}
            rowsPerPage={rowsPerPage}
            page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            labelRowsPerPage="Registros por página:"
            labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} de ${count}`
            }
          />
        </Paper>

          {/* Dialog para criar/editar */}
          <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {editingMapeamento ? <EditIcon color="primary" /> : <AddIcon color="primary" />}
                {editingMapeamento ? 'Editar Mapeamento' : 'Novo Mapeamento'}
              </Box>
          </DialogTitle>
          <DialogContent>
              <Box sx={{ pt: 2 }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    Configure como uma empresa do sistema aparece nos arquivos de uma seguradora específica.
                    Cada combinação empresa-seguradora deve ser única.
                </Typography>
              </Alert>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                                <Autocomplete
                  options={empresas}
                  getOptionLabel={(option) => option.nome || ''}
                  value={empresas.find(e => e.id === parseInt(formData.empresa_id)) || null}
                  filterOptions={(options, { inputValue }) => {
                    const searchTerm = inputValue.toLowerCase();
                    return options.filter(option => 
                      String(option.id).includes(searchTerm) ||
                      (option.nomeFantasia || '').toLowerCase().includes(searchTerm) ||
                      (option.nome || '').toLowerCase().includes(searchTerm)
                    );
                  }}
                  onChange={(event, newValue) => {
                        setFormData(prev => ({
                          ...prev,
                          empresa_id: newValue ? newValue.id.toString() : ''
                        }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                          label="Empresa"
                      required
                          helperText="Selecione a empresa do sistema"
                      InputProps={{
                        ...params.InputProps,
                            startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  )}
                />
              </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={seguradoras}
                      getOptionLabel={(option) => option.nome || ''}
                      value={seguradoras.find(s => s.id === parseInt(formData.seguradora_id)) || null}
                      onChange={(event, newValue) => {
                        setFormData(prev => ({
                          ...prev,
                          seguradora_id: newValue ? newValue.id.toString() : ''
                        }));
                      }}
                      renderInput={(params) => (
                <TextField
                          {...params}
                          label="Seguradora"
                          required
                          helperText="Selecione a seguradora"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: <AccountBalanceIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          }}
                        />
                      )}
                />
              </Grid>
                  <Grid item xs={12}>
                <TextField
                  fullWidth
                      label="Razão Social na Seguradora"
                      name="razao_social_seguradora"
                      value={formData.razao_social_seguradora}
                      onChange={handleInputChange}
                      required
                      helperText="Exatamente como a empresa aparece nos arquivos desta seguradora (case-sensitive)"
                      multiline
                      rows={3}
                      placeholder="Ex: ACME CORRETORA DE SEGUROS LTDA"
                />
              </Grid>
            </Grid>
              </Box>
          </DialogContent>
          <DialogActions>
              <Button onClick={handleCloseDialog} color="inherit">
                Cancelar
              </Button>
              <Button onClick={handleSave} variant="contained">
                {editingMapeamento ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </Dialog>
        </Box>
      </Container>
    </Layout>
  );
};

export default DeParaPanel; 